import { get, del, post, type ApiError } from "@/shared/utils/fetchApi";

interface ApiErrorData {
  code?: string;
}

/**
 * Interface para representar um convite
 */
export interface Invite {
  id: string;
  email: string;
  role: string | null;
  status: string;
  avatar_url: string | null;
  organization_id: string | null;
  created_by: string | null;
  created_at: string;
  user_name: string | null;
  product_ids: string[];
}

/**
 * O que `POST /invites` devolve ao criar: resposta parcial, não o `Invite`
 * completo. Declarado à parte porque tipar como `Invite` prometia `status`,
 * `organization_id` e afins que não vêm no corpo — quem confiasse na assinatura
 * lia `undefined`.
 */
export interface CreateInviteResponse {
  id: string;
  email: string;
  token: string;
}

/**
 * Interface para criar um novo convite
 */
export interface CreateInviteDTO {
  email: string;
  role?: string;
  organization_id?: string;
  user_name?: string;
  product_ids?: string[];
  /**
   * Idioma do email do convite. Sem ele o backend assume inglês, e todo convite saía
   * em inglês independentemente do idioma em uso (AB#84509).
   */
  locale?: string;
}

/**
 * Parâmetros de filtro para listagem de convites
 */
export interface InviteFilterParams {
  email?: string;
  status?: string;
  organization_id?: string;
  created_by?: string;
}

/**
 * Classe de serviço para encapsular todas as operações relacionadas aos convites
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class InviteService {
  /**
   * Lista todos os convites com possibilidade de filtro
   * @param filters Parâmetros de filtro opcionais
   * @returns Lista de convites
   */
  static async listInvites(filters?: InviteFilterParams): Promise<Invite[]> {
    try {
      // Se temos apenas um filtro de email e não temos organization_id, usamos um endpoint específico
      // para verificar a existencia de convites por email - Importante para verificar duplicados
      if (
        filters?.email &&
        !filters.organization_id &&
        !filters.status &&
        !filters.created_by
      ) {
        const queryParams = new URLSearchParams();
        queryParams.append("email", filters.email);

        // v2 não tem /check-email; GET /invites?email= faz o mesmo ilike. Envelope idêntico.
        const url = `/invites?${queryParams.toString()}`;

        try {
          const result = await get<{ data: Invite[]; success: boolean }>(url);
          return result.data || [];
        } catch (error) {
          // Só cai para o fluxo padrão quando a rota não existe (404/501). Um
          // 500 aqui era engolido e o resultado vinha da listagem completa, o
          // que escondia a falha e devolvia dados diferentes dos pedidos.
          const status = (error as ApiError)?.status;
          if (status !== 404 && status !== 501) {
            throw error;
          }
          console.warn(
            "Rota de verificação por email indisponível, usando a listagem padrão:",
            error,
          );
        }
      }

      // Se temos um organization_id específico, usamos o endpoint normal com query params
      if (filters?.organization_id) {
        const queryParams = new URLSearchParams();

        if (filters.email) queryParams.append("email", filters.email);
        if (filters.status) queryParams.append("status", filters.status);
        if (filters.organization_id)
          queryParams.append("organization_id", filters.organization_id);
        if (filters.created_by)
          queryParams.append("created_by", filters.created_by);

        const queryString = queryParams.toString();
        const url = `/invites${queryString ? `?${queryString}` : ""}`;

        const result = await get<{ data: Invite[]; success: boolean }>(url);
        return result.data || [];
      }
      // Caso contrário, usamos o endpoint JWT para obter todos os convites da organização atual
      else {
        const result = await get<{ data: Invite[]; success: boolean }>(
          "/invites/organization/current",
        );

        // Se estamos filtrando por email, fazemos o filtro do lado do cliente
        if (filters?.email && result.data && result.data.length > 0) {
          return result.data.filter(
            (invite) =>
              invite.email.toLowerCase() === filters.email?.toLowerCase(),
          );
        }

        return result.data || [];
      }
    } catch (error) {
      console.error("Erro ao listar convites:", error);
      throw error;
    }
  }

  /**
   * Obtém um convite específico pelo ID
   * @param id ID do convite
   * @returns Detalhes do convite
   */
  static async getInviteById(id: string): Promise<Invite> {
    try {
      const result = await get<{
        data: Invite;
        success: boolean;
        message?: string;
      }>(`/invites/${id}`);

      if (!result.success) {
        throw new Error(result.message || "Erro ao obter convite");
      }

      return result.data;
    } catch (error) {
      console.error(`Erro ao obter convite com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Cria um novo convite
   * @param inviteData Dados do novo convite
   * @returns `{id, email, token}` — a rota não devolve o convite completo
   */
  static async createInvite(
    inviteData: CreateInviteDTO,
  ): Promise<CreateInviteResponse> {
    try {
      // Verificar e garantir que os dados estão formatados corretamente
      const formattedData = {
        ...inviteData,
        // Garantir que o email está em minúsculas
        email: inviteData.email.toLowerCase(),
        // Garantir que o role está em minúsculas
        role: inviteData.role?.toLowerCase(),
        // Garantir que product_ids seja um array válido
        product_ids: Array.isArray(inviteData.product_ids)
          ? inviteData.product_ids
          : [],
      };

      // Validar campos obrigatórios antes de enviar para o backend
      if (!formattedData.organization_id) {
        throw new Error("organization_id_missing");
      }

      // Payload camelCase; `createdBy` vem do JWT, não do body.
      const result = await post<{
        data: CreateInviteResponse;
        success: boolean;
        message?: string;
      }>("/invites", {
        email: formattedData.email,
        role: formattedData.role,
        organizationId: formattedData.organization_id,
        productIds: formattedData.product_ids,
        userName: formattedData.user_name,
        // Sem isto o backend assume inglês e o convite sai em inglês mesmo com a
        // plataforma em português (AB#84509).
        locale: formattedData.locale,
      });

      if (!result.success) {
        throw new Error(result.message || "Erro ao criar convite");
      }

      return result.data;
    } catch (error: unknown) {
      console.error("Erro ao criar convite:", error);

      if (error instanceof Error) {
        const apiError = error as ApiError;

        // Erro de duplicidade (409). Checa status (v2 não embute "409" na message);
        // mantém o fallback de substring p/ o path legacy.
        if (apiError.status === 409 || error.message.includes("409")) {
          const code = (apiError.data as ApiErrorData)?.code;
          if (code === "USER_IN_ANOTHER_ORG") {
            throw new Error("user_in_another_org");
          }
          throw new Error("duplicate_invite");
        }

        // Erro de organização não encontrada
        if (error.message.includes("organization_id_missing")) {
          throw new Error("organization_id_missing");
        }

        // Erro 500 - pode ser problema com o formato dos dados
        if (apiError.status === 500 || error.message.includes("500")) {
          console.error(
            "[InviteService] Erro 500 do servidor. Verificar formato dos dados enviados.",
          );
          throw new Error("server_error");
        }
      }

      throw error;
    }
  }

  /**
   * Exclui um convite
   * @param id ID do convite a ser excluído
   * @returns Mensagem de sucesso
   */
  static async deleteInvite(id: string): Promise<string> {
    try {
      // Usar o endpoint REST em vez de acesso direto ao Supabase
      const result = await del<{ success: boolean; message: string }>(
        `/invites/${id}`,
      );

      if (!result.success) {
        throw new Error(result.message || "Erro ao excluir convite");
      }

      return result.message;
    } catch (error) {
      console.error(`Erro ao excluir convite com ID ${id}:`, error);

      // Repassa o erro original: recriar um `Error` com a mesma mensagem só
      // descartava o stack de onde a chamada falhou.
      throw error;
    }
  }
}
